import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Grid, Paper, Typography, Button, 
  Tabs, Tab, Card, CardContent, CardHeader, Chip,
  Avatar, IconButton, Divider, useTheme, alpha,
  CircularProgress
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  AutoAwesome as AutoAwesomeIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { marketingService, Product } from '../services/marketingService';
import { Lead, Campaign } from '../types/marketing';
import { useCreateNotification } from '../hooks/useCreateNotification';
import { NOTIFICATION_TYPES } from '../utils/notificationUtils';

// Componentes que serão criados posteriormente
// import CampaignEditor from '../components/marketing/CampaignEditor';
// import LeadKanbanBoard from '../components/marketing/LeadKanbanBoard';
// import AIContentGenerator from '../components/marketing/AIContentGenerator';
// import MarketingMetrics from '../components/marketing/MarketingMetrics';
// import AutomationBuilder from '../components/marketing/AutomationBuilder';
// import ProductCatalog from '../components/marketing/ProductCatalog';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
  color: '#ffffff',
  fontWeight: 'bold',
  padding: '10px 20px',
  borderRadius: 8,
  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 6px 15px rgba(0, 0, 0, 0.2)',
    transform: 'translateY(-2px)',
  },
}));

const MetricCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  borderRadius: 16,
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
}));

// Componente principal
const MarketingPagePrototype: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const createNotification = useCreateNotification();
  
  // Carregar dados iniciais
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [leadsData, productsData] = await Promise.all([
          marketingService.getLeads(),
          marketingService.getProducts()
        ]);
        setLeads(leadsData);
        setProducts(productsData);
        
        // Dados fictícios para campanhas (seriam carregados de uma API real)
        const mockCampaigns: Campaign[] = [
          { 
            id: '1', 
            name: 'Lançamento Residencial Vista Verde', 
            type: 'email', 
            subject: 'Conheça o novo conceito em moradia', 
            content: 'Conteúdo da campanha...',
            status: 'scheduled',
            scheduledDates: [new Date().toISOString()],
            recipients: ['cliente1@email.com', 'cliente2@email.com'],
            metrics: {
              sent: 120,
              delivered: 100,
              opened: 68,
              clicked: 32,
              failed: 20
            }
          },
          { 
            id: '2', 
            name: 'Promoção Especial - Imóveis Comerciais', 
            type: 'whatsapp', 
            subject: '', 
            content: 'Conteúdo da campanha...',
            status: 'scheduled',
            scheduledDates: [new Date(Date.now() + 86400000).toISOString()],
            recipients: ['+5511999999999', '+5511888888888'],
            metrics: {
              sent: 0,
              delivered: 0,
              opened: 0,
              clicked: 0,
              failed: 0
            }
          },
          { 
            id: '3', 
            name: 'Follow-up Clientes Interessados', 
            type: 'email', 
            subject: 'Informações adicionais sobre seu interesse', 
            content: 'Conteúdo da campanha...',
            status: 'sent',
            scheduledDates: [new Date(Date.now() - 86400000).toISOString()],
            recipients: ['cliente3@email.com', 'cliente4@email.com'],
            metrics: {
              sent: 85,
              delivered: 80,
              opened: 42,
              clicked: 18,
              failed: 5
            }
          }
        ];
        setCampaigns(mockCampaigns);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        createNotification({
          ...NOTIFICATION_TYPES.SYSTEM.ERROR,
          message: 'Erro ao carregar dados de marketing. Tente novamente.'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [createNotification]);
  
  // Renderizar o Dashboard
  const renderDashboard = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <MetricCard>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Campanhas Ativas
            </Typography>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {campaigns.filter(c => c.status === 'scheduled').length}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              de {campaigns.length} campanhas totais
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Taxa de Abertura
            </Typography>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {Math.round(
                (campaigns.reduce((sum, c) => sum + (c.metrics?.opened || 0), 0) / 
                campaigns.reduce((sum, c) => sum + (c.metrics?.sent || 0), 0)) * 100 || 0
              )}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              média de todas as campanhas
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Leads Ativos
            </Typography>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {leads.length}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              leads cadastrados no sistema
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Taxa de Conversão
            </Typography>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {Math.round(
                (campaigns.reduce((sum, c) => sum + (c.metrics?.clicked || 0) * 0.25, 0) / 
                campaigns.reduce((sum, c) => sum + (c.metrics?.clicked || 0), 0)) * 100 || 0
              )}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              dos cliques resultam em conversão
            </Typography>
          </MetricCard>
        </Grid>
      </Grid>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <StyledCard>
            <CardHeader 
              title="Campanhas Recentes" 
              action={
                <GradientButton 
                  startIcon={<AddIcon />}
                  onClick={() => setActiveTab(1)}
                >
                  Nova Campanha
                </GradientButton>
              }
            />
            <CardContent>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {campaigns.map(campaign => (
                  <Box 
                    key={campaign.id}
                    sx={{ 
                      p: 2, 
                      mb: 2, 
                      borderRadius: 2, 
                      bgcolor: 'background.default',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: campaign.type === 'email' ? 'primary.main' : 'secondary.main',
                          mr: 2
                        }}
                      >
                        {campaign.type === 'email' ? <EmailIcon /> : <WhatsAppIcon />}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {campaign.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {campaign.type === 'email' ? 'E-mail' : 'WhatsApp'} • 
                          {campaign.status === 'scheduled' ? ' Agendada' : 
                           campaign.status === 'sent' ? ' Enviada' : ' Rascunho'} • 
                          {campaign.recipients.length} destinatários
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Chip 
                        label={
                          campaign.status === 'scheduled' ? 'Agendada' : 
                          campaign.status === 'sent' ? 'Enviada' : 
                          campaign.status === 'failed' ? 'Falhou' : 'Rascunho'
                        }
                        color={
                          campaign.status === 'scheduled' ? 'primary' : 
                          campaign.status === 'sent' ? 'success' : 
                          campaign.status === 'failed' ? 'error' : 'default'
                        }
                        size="small"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <StyledCard>
            <CardHeader 
              title="Leads Recentes" 
              action={
                <IconButton onClick={() => setActiveTab(2)}>
                  <PeopleIcon />
                </IconButton>
              }
            />
            <CardContent>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {leads.slice(0, 5).map(lead => (
                  <Box 
                    key={lead.id}
                    sx={{ 
                      p: 2, 
                      mb: 2, 
                      borderRadius: 2, 
                      bgcolor: 'background.default',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Avatar 
                      sx={{ mr: 2 }}
                      alt={lead.name}
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(lead.name)}&background=random`}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {lead.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {lead.email} • {lead.source}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
  
  // Renderizar o conteúdo baseado na tab ativa
  const renderContent = () => {
    switch (activeTab) {
      case 0: // Dashboard
        return renderDashboard();
      case 1: // Campanhas
        return (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" gutterBottom>
              Criador de Campanhas
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
              Esta seção está em desenvolvimento. Aqui você poderá criar e gerenciar suas campanhas.
            </Typography>
            <GradientButton startIcon={<AddIcon />}>
              Nova Campanha
            </GradientButton>
          </Box>
        );
      case 2: // Leads
        return (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" gutterBottom>
              Gerenciamento de Leads
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
              Esta seção está em desenvolvimento. Aqui você poderá visualizar e gerenciar seus leads.
            </Typography>
            <GradientButton startIcon={<AddIcon />}>
              Novo Lead
            </GradientButton>
          </Box>
        );
      case 3: // Automação
        return (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" gutterBottom>
              Automação de Marketing
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
              Esta seção está em desenvolvimento. Aqui você poderá criar fluxos de automação para suas campanhas.
            </Typography>
            <GradientButton startIcon={<AutoAwesomeIcon />}>
              Criar Automação
            </GradientButton>
          </Box>
        );
      case 4: // Análise
        return (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" gutterBottom>
              Análise de Campanhas
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
              Esta seção está em desenvolvimento. Aqui você poderá analisar o desempenho de suas campanhas.
            </Typography>
            <GradientButton startIcon={<AnalyticsIcon />}>
              Ver Relatórios
            </GradientButton>
          </Box>
        );
      default:
        return renderDashboard();
    }
  };
  
  return (
    <Box sx={{ 
      bgcolor: alpha(theme.palette.background.default, 0.8),
      minHeight: '100vh',
      pb: 8
    }}>
      {/* Header com métricas rápidas */}
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: 0,
          mb: 3,
          py: 3,
          background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: '#ffffff'
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" fontWeight="bold">Marketing</Typography>
              <Typography variant="subtitle1">
                Gerencie suas campanhas, leads e automações
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <GradientButton
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ 
                  bgcolor: '#ffffff', 
                  color: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: alpha('#ffffff', 0.9)
                  }
                }}
                onClick={() => setActiveTab(1)}
              >
                Nova Campanha
              </GradientButton>
            </Grid>
          </Grid>
        </Container>
      </Paper>
      
      {/* Navegação principal */}
      <Container maxWidth="xl">
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            mb: 4,
            '& .MuiTab-root': {
              minWidth: 120,
              fontWeight: 500,
              fontSize: '1rem',
              textTransform: 'none',
            }
          }}
        >
          <Tab icon={<DashboardIcon />} label="Dashboard" iconPosition="start" />
          <Tab icon={<CampaignIcon />} label="Campanhas" iconPosition="start" />
          <Tab icon={<PeopleIcon />} label="Leads" iconPosition="start" />
          <Tab icon={<AutoAwesomeIcon />} label="Automação" iconPosition="start" />
          <Tab icon={<AnalyticsIcon />} label="Análise" iconPosition="start" />
        </Tabs>
        
        {/* Conteúdo principal */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {renderContent()}
          </motion.div>
        )}
      </Container>
    </Box>
  );
};

export default MarketingPagePrototype;
